import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { format, isValid } from "date-fns";
import { toast } from "sonner";
import type { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Eye,
  FileText,
  LoaderCircle,
  ShieldCheck,
  User,
  Users,
  WalletCards,
  XCircle,
} from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";

type LoanTab = "loans" | "apply" | "guarantors";

const statusClasses: Record<string, string> = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  disbursed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  completed: "border-slate-200 bg-slate-50 text-slate-700",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
  upcoming: "border-blue-200 bg-blue-50 text-blue-700",
  pending: "border-amber-200 bg-amber-50 text-amber-700",
  pending_approval: "border-amber-200 bg-amber-50 text-amber-700",
  pending_guarantor_approval: "border-violet-200 bg-violet-50 text-violet-700",
  rejected: "border-red-200 bg-red-50 text-red-700",
  defaulted: "border-red-200 bg-red-50 text-red-700",
};

const asNumber = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const asDate = (value: any): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return isValid(parsed) ? parsed : null;
};

const money = (value: any) => `${Math.round(asNumber(value)).toLocaleString()} FRW`;
const dateLabel = (value: any) => {
  const parsed = asDate(value);
  return parsed ? format(parsed, "dd MMM yyyy") : "-";
};
const statusKey = (value: any) => String(value || "").toLowerCase().replace(/\s+/g, "_");

const getLoanCode = (loan: any) => loan?.loanCode || loan?.referenceNumber || `LN-${String(loan?.id || "").padStart(4, "0")}`;
const getLoanType = (loan: any) => loan?.loanType?.name || loan?.loanType || loan?.type || "-";
const getApprovedAmount = (loan: any) => asNumber(loan?.amount ?? loan?.approvedAmount ?? loan?.loanAmount);
const getNetReceived = (loan: any) => {
  const explicit = asNumber(loan?.disbursedAmount ?? loan?.amountDisbursed ?? loan?.netAmount);
  if (explicit > 0) return explicit;
  const approved = getApprovedAmount(loan);
  const upfrontInterest = asNumber(loan?.upfrontInterest ?? loan?.interestDeducted);
  return Math.max(0, approved - upfrontInterest);
};
const getBalance = (loan: any) => asNumber(loan?.outstandingBalance ?? loan?.dueAmount ?? loan?.remainingBalance);
const getTerm = (loan: any) => asNumber(loan?.termMonths ?? loan?.totalMonths ?? loan?.months);
const getStartDate = (loan: any) => loan?.disbursementDate || loan?.approvedAt || loan?.approvalDate || loan?.createdAt;
const getEndDate = (loan: any) => loan?.maturityDate || loan?.endDate;
const getPayments = (loan: any) => {
  if (Array.isArray(loan?.payments)) return loan.payments;
  if (Array.isArray(loan?.repayments)) return loan.repayments;
  if (Array.isArray(loan?.recentPayments)) return loan.recentPayments;
  return [];
};
const getSchedule = (loan: any) => {
  if (Array.isArray(loan?.schedule)) return loan.schedule;
  if (Array.isArray(loan?.repaymentSchedule)) return loan.repaymentSchedule;
  return [];
};

export default function MyLoans() {
  const { user, portalMode } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<LoanTab>("loans");
  const [selectedLoanType, setSelectedLoanType] = useState("");
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState("");
  const [purpose, setPurpose] = useState("");

  const { data: loansData, isLoading: loansLoading } = useQuery(
    ["my-loans", portalMode, user?.id],
    async () => {
      const endpoint = portalMode === "member" ? "/loans/my" : `/teachers/${user?.id}/loans`;
      const { data: res } = await api.get(endpoint);
      const result = res.data || res;
      return {
        loans: Array.isArray(result) ? result : result?.loans || [],
        totalBorrowed: asNumber(result?.totalBorrowed),
        totalRepaid: asNumber(result?.totalRepaid),
        outstandingBalance: asNumber(result?.outstandingBalance),
        activeLoans: asNumber(result?.activeLoans),
      };
    },
    { enabled: !!user?.id }
  );

  const { data: settings } = useQuery(
    ["my-loans-settings"],
    async () => {
      const { data } = await api.get("/settings");
      return data.data || data;
    },
    { staleTime: 300000 }
  );

  const { data: contributions = [] } = useQuery(
    ["my-loans-contributions", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=2000");
      const result = data.data || data;
      return Array.isArray(result) ? result : result?.contributions || [];
    },
    { enabled: !!user?.id }
  );

  const { data: memberPayments = [] } = useQuery(
    ["my-loans-payments", user?.id],
    async () => {
      const { data } = await api.get("/member-payments/my?limit=200").catch(() => ({ data: [] }));
      return Array.isArray(data) ? data : data?.data || data?.results || [];
    },
    { enabled: !!user?.id }
  );

  const { data: loanTypes = [], isLoading: loanTypesLoading } = useQuery(
    ["my-loans-loan-types"],
    async () => {
      const { data } = await api.get("/loan-types");
      const result = data.data || data;
      return (Array.isArray(result) ? result : []).filter((type: any) => type.isActive !== false);
    },
    { enabled: !!user?.id }
  );

  const { data: guarantorData = [], isLoading: guarantorsLoading } = useQuery(
    ["my-guarantor-requests", user?.id],
    async () => {
      const { data: res } = await api.get("/loans/my/guarantor-requests");
      return res.data || res.requests || res || [];
    },
    { enabled: !!user?.id }
  );

  const respondMutation = useMutation(
    async ({ id, action }: { id: number; action: "approve" | "reject" }) => {
      const { data } = await api.patch(`/loans/guarantors/${id}/respond`, { action });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["my-guarantor-requests", user?.id]);
        queryClient.invalidateQueries(["my-loans"]);
        toast.success("Guarantor response submitted");
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Failed to submit guarantor response");
      },
    }
  );

  const loans = loansData?.loans || [];
  const activeLoans = loans.filter((loan: any) => ["active", "disbursed"].includes(statusKey(loan.status)));
  const historyLoans = loans.filter((loan: any) => !["active", "disbursed"].includes(statusKey(loan.status)));
  const primaryActiveLoan = activeLoans[0];
  const pendingGuarantorRequests = (Array.isArray(guarantorData) ? guarantorData : []).filter(
    (request: any) => statusKey(request.status) === "pending"
  );

  const verifiedSavings = useMemo(() => {
    const contributionsTotal = contributions
      .filter((item: any) => {
        const status = statusKey(item?.status || item?.verificationStatus);
        return item?.isVerified || item?.verified || status === "verified" || status === "approved";
      })
      .reduce((sum: number, item: any) => sum + asNumber(item?.amount ?? item?.depositAmount), 0);

    const approvedSavingsTotal = memberPayments
      .filter((p: any) => String(p.status || "").toLowerCase() === "approved")
      .reduce((sum: number, p: any) => sum + asNumber(p.amount), 0);

    return contributionsTotal + approvedSavingsTotal;
  }, [contributions, memberPayments]);

  const shareValue = asNumber(settings?.savings?.shareValue);
  const totalShares = shareValue > 0 ? verifiedSavings / shareValue : 0;
  const loanPolicies = settings?.loanPolicies || {};
  const maxLoanMultiplier = asNumber(loanPolicies?.maxLoanMultiplier);
  const maximumLoanLimit = maxLoanMultiplier > 0 ? verifiedSavings * maxLoanMultiplier : 0;
  const availableLoanLimit = Math.max(0, maximumLoanLimit - asNumber(loansData?.outstandingBalance));
  const totalLoanReceived = loans.reduce((sum: number, loan: any) => sum + getNetReceived(loan), 0);
  const totalBalance = asNumber(loansData?.outstandingBalance) || activeLoans.reduce((sum: number, loan: any) => sum + getBalance(loan), 0);
  const totalRepaid = asNumber(loansData?.totalRepaid) || loans.reduce((sum: number, loan: any) => {
    return sum + getPayments(loan).reduce((paymentSum: number, payment: any) => paymentSum + asNumber(payment?.amount ?? payment?.paidAmount), 0);
  }, 0);
  const nextScheduleItem = getSchedule(primaryActiveLoan).find((item: any) => statusKey(item.status) !== "paid");
  const nextInstallment = asNumber(
    nextScheduleItem?.totalAmount ??
      nextScheduleItem?.totalDue ??
      nextScheduleItem?.amount ??
      primaryActiveLoan?.monthlyPayment ??
      primaryActiveLoan?.installment
  );
  const repaymentRows = getSchedule(primaryActiveLoan).slice(0, 6);

  const applyButtonDisabled = !selectedLoanType && loanTypes.length > 0;
  const goToApply = () => navigate("/member/loans/apply");
  const goToLoan = (loan: any) => navigate(`/member/loans/${loan.loanCode || loan.id}`);
  const goToRepay = () => {
    if (primaryActiveLoan) {
      navigate(`/member/loans/${primaryActiveLoan.loanCode || primaryActiveLoan.id}`);
      return;
    }
    navigate("/member/savings/record");
  };

  if (loansLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 text-[#07184f]">
      <div>
        <h1 className="text-2xl font-bold tracking-normal">My Loans</h1>
        <p className="text-sm text-[#4f5f8b]">Apply for a new loan or manage your existing loans</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Total Loan Received" value={money(totalLoanReceived)} helper={`${activeLoans.length} active loan${activeLoans.length === 1 ? "" : "s"}`} subHelper="Net amount received" tone="green" icon={<Users className="h-5 w-5" />} />
        <MetricCard label="Total Loan Balance" value={money(totalBalance)} helper="Remaining to repay" actionLabel="Repay Loan" onAction={goToRepay} tone="blue" icon={<WalletCards className="h-5 w-5" />} />
        <MetricCard label="Total Repaid" value={money(totalRepaid)} helper="Amount already paid" subHelper={totalLoanReceived > 0 ? `${Math.round((totalRepaid / Math.max(totalLoanReceived, 1)) * 100)}% of total loan` : undefined} tone="orange" icon={<Users className="h-5 w-5" />} />
        <MetricCard label="Next Instalment" value={money(nextInstallment)} helper={nextScheduleItem?.dueDate ? `Due on: ${dateLabel(nextScheduleItem.dueDate)}` : "No due date"} subHelper={nextScheduleItem?.dueDate ? daysUntil(nextScheduleItem.dueDate) : undefined} tone="purple" icon={<Calendar className="h-5 w-5" />} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="space-y-4">
          <Card className="border-slate-200 shadow-sm">
            <div className="flex border-b border-slate-200 px-4">
              <TabButton active={activeTab === "loans"} onClick={() => setActiveTab("loans")}>My Loans</TabButton>
              <TabButton active={false} onClick={goToApply}>Apply for Loan</TabButton>
              <TabButton active={activeTab === "guarantors"} onClick={() => setActiveTab("guarantors")}>
                Guarantor Requests
                {pendingGuarantorRequests.length > 0 && <span className="ml-2 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">{pendingGuarantorRequests.length}</span>}
              </TabButton>
            </div>
            <CardContent className="p-4">
              {activeTab === "loans" && (
                <div className="space-y-4">
                  <LoanTable title="My Active Loans" loans={activeLoans} emptyText="You have no active loans." onView={goToLoan} />
                  <LoanTable title="My Loan History" loans={historyLoans} emptyText="No loan history found." onView={goToLoan} />
                </div>
              )}

              {activeTab === "apply" && (
                <div className="grid gap-4 lg:grid-cols-2">
                  <div>
                    <h2 className="mb-4 text-base font-bold">Apply for a New Loan</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Type of Loan" required>
                        <Select value={selectedLoanType} onValueChange={setSelectedLoanType}>
                          <SelectTrigger className="h-10"><SelectValue placeholder={loanTypesLoading ? "Loading..." : "Select Loan Type"} /></SelectTrigger>
                          <SelectContent>
                            {loanTypes.map((type: any) => (
                              <SelectItem key={type.id} value={String(type.id)}>{type.name || type.displayName || `Loan Type ${type.id}`}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Loan Amount (FRW)" required><Input value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="Enter amount" /></Field>
                      <Field label="Number of Months" required><Input value={months} onChange={(event) => setMonths(event.target.value)} placeholder="Enter months" /></Field>
                      <Field label="Purpose of Loan" required><Input value={purpose} onChange={(event) => setPurpose(event.target.value)} placeholder="Enter purpose" /></Field>
                    </div>
                    <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <SummaryLine label="Your Total Savings" value={money(verifiedSavings)} />
                        <SummaryLine label="Maximum Loan Limit" value={maxLoanMultiplier > 0 ? money(maximumLoanLimit) : "Not configured"} />
                        <SummaryLine label="Available Limit" value={maxLoanMultiplier > 0 ? money(availableLoanLimit) : "Not configured"} />
                        <SummaryLine label="Active Balance" value={money(totalBalance)} />
                      </div>
                    </div>
                    <div className="mt-4 flex gap-3">
                      <Button variant="outline" className="flex-1" onClick={() => { setSelectedLoanType(""); setAmount(""); setMonths(""); setPurpose(""); }}>Clear</Button>
                      <Button className="flex-1 bg-[#009A44] hover:bg-[#00843a]" onClick={goToApply} disabled={applyButtonDisabled}>Continue Application</Button>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-4">
                    <h2 className="text-base font-bold">Add Guarantors</h2>
                    <p className="mt-1 text-sm text-[#4f5f8b]">Guarantor selection and final submission are completed in the secure application flow.</p>
                    <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
                      <FileText className="mb-2 h-5 w-5" />
                      Your loan application will be sent to your selected guarantors for approval before management review.
                    </div>
                    <Button variant="outline" className="mt-4 w-full" onClick={goToApply}>Open Full Application <ArrowRight className="ml-2 h-4 w-4" /></Button>
                  </div>
                </div>
              )}

              {activeTab === "guarantors" && (
                <GuarantorRequests requests={pendingGuarantorRequests} loading={guarantorsLoading} responding={respondMutation.isLoading} onRespond={(id, action) => respondMutation.mutate({ id, action })} onView={(loan) => loan && goToLoan(loan)} />
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base">Loan Repayment Schedule (Active Loans)</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Instalment No.</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Amount Due (FRW)</TableHead>
                      <TableHead className="text-right">Amount Paid (FRW)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Remaining Balance (FRW)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {repaymentRows.length > 0 ? repaymentRows.map((row: any, index: number) => (
                      <TableRow key={row.id || index}>
                        <TableCell>{row.installmentNumber || index + 1}</TableCell>
                        <TableCell>{dateLabel(row.dueDate)}</TableCell>
                        <TableCell className="text-right font-semibold">{money(row.totalAmount ?? row.totalDue ?? row.amount)}</TableCell>
                        <TableCell className="text-right">{money(row.paidAmount)}</TableCell>
                        <TableCell><LoanStatusBadge status={row.status || (asNumber(row.paidAmount) > 0 ? "paid" : "upcoming")} /></TableCell>
                        <TableCell className="text-right font-semibold">{money(row.remainingBalance)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={6} className="h-24 text-center text-sm text-[#4f5f8b]">No active repayment schedule found.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">Loan Limit & Summary</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <SummaryLine label="Total Savings (Shares)" value={`${Number(totalShares.toFixed(2)).toLocaleString()} Shares`} />
              <SummaryLine label="Total Savings Value" value={money(verifiedSavings)} />
              <SummaryLine label="Maximum Loan Limit" value={maxLoanMultiplier > 0 ? money(maximumLoanLimit) : "Not configured"} strong />
              <div className="h-px bg-slate-200" />
              <SummaryLine label="Total Outstanding Loans" value={money(totalBalance)} />
              <SummaryLine label="Available Loan Limit" value={maxLoanMultiplier > 0 ? money(availableLoanLimit) : "Not configured"} success />
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
                <div className="mb-2 flex items-center gap-2 font-bold text-emerald-800"><ShieldCheck className="h-4 w-4" />Policy Reminder</div>
                <p className="text-xs text-emerald-800">{maxLoanMultiplier > 0 ? `No member can apply for a loan greater than ${maxLoanMultiplier} times their total savings.` : "Loan multiplier policy is not configured in system settings."}</p>
              </div>
              <Button className="w-full bg-[#009A44] hover:bg-[#00843a]" onClick={goToRepay}>Repay Loan</Button>
              <Button variant="outline" className="w-full" onClick={() => primaryActiveLoan ? goToLoan(primaryActiveLoan) : setActiveTab("loans")}>Repayment History</Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                Guarantor Requests
                {pendingGuarantorRequests.length > 0 && <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">{pendingGuarantorRequests.length}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <GuarantorRequests requests={pendingGuarantorRequests.slice(0, 1)} loading={guarantorsLoading} responding={respondMutation.isLoading} compact onRespond={(id, action) => respondMutation.mutate({ id, action })} onView={(loan) => loan && goToLoan(loan)} />
              {pendingGuarantorRequests.length > 1 && <Button variant="outline" className="mt-3 w-full" onClick={() => setActiveTab("guarantors")}>View All Requests <ArrowRight className="ml-2 h-4 w-4" /></Button>}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function MetricCard({ label, value, helper, subHelper, actionLabel, onAction, tone, icon }: { label: string; value: string; helper?: string; subHelper?: string; actionLabel?: string; onAction?: () => void; tone: "green" | "blue" | "orange" | "purple"; icon: React.ReactNode }) {
  const styles = {
    green: { card: "border-emerald-100", icon: "bg-[#009A44] text-white", text: "text-[#009A44]" },
    blue: { card: "border-blue-100", icon: "bg-blue-600 text-white", text: "text-blue-600" },
    orange: { card: "border-orange-100", icon: "bg-orange-500 text-white", text: "text-orange-600" },
    purple: { card: "border-violet-100", icon: "bg-violet-600 text-white", text: "text-violet-600" },
  }[tone];
  return (
    <Card className={`shadow-sm ${styles.card}`}>
      <CardContent className="flex gap-4 p-5">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${styles.icon}`}>{icon}</div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-[#07184f]">{label}</p>
          <p className={`mt-2 text-xl font-bold ${styles.text}`}>{value}</p>
          {helper && <p className="mt-1 text-xs font-medium text-[#4f5f8b]">{helper}</p>}
          {subHelper && <p className="mt-2 text-xs text-[#4f5f8b]">{subHelper}</p>}
          {actionLabel && <Button variant="outline" size="sm" className="mt-3 h-8 w-full" onClick={onAction}>{actionLabel}</Button>}
        </div>
      </CardContent>
    </Card>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button onClick={onClick} className={`flex items-center border-b-2 px-5 py-3 text-sm font-bold transition-colors ${active ? "border-[#009A44] text-[#009A44]" : "border-transparent text-[#4f5f8b] hover:text-[#07184f]"}`}>{children}</button>;
}

function LoanTable({ title, loans, emptyText, onView }: { title: string; loans: any[]; emptyText: string; onView: (loan: any) => void }) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 5 });

  const paginatedLoans = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return loans.slice(start, start + pagination.pageSize);
  }, [loans, pagination.pageIndex, pagination.pageSize]);

  const pageCount = Math.max(1, Math.ceil(loans.length / pagination.pageSize));

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: "loanCode",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Loan ID" />,
      cell: ({ row }) => <span className="font-mono text-xs font-bold">{getLoanCode(row.original)}</span>,
    },
    {
      id: "loanType",
      accessorFn: (row) => getLoanType(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type of Loan" />,
      cell: ({ row }) => <span className="font-medium">{getLoanType(row.original)}</span>,
    },
    {
      id: "approvedAmount",
      accessorFn: (row) => getApprovedAmount(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Approved Amount" />,
      cell: ({ row }) => <div className="text-right font-semibold">{getApprovedAmount(row.original).toLocaleString()}</div>,
    },
    {
      id: "netReceived",
      accessorFn: (row) => getNetReceived(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Net Amount Received" />,
      cell: ({ row }) => {
        const loan = row.original;
        return (
          <div className="text-right">
            <div className="font-semibold">{getNetReceived(loan).toLocaleString()}</div>
            {getApprovedAmount(loan) > getNetReceived(loan) && (
              <div className="text-[11px] text-[#4f5f8b]">(After deductions)</div>
            )}
          </div>
        );
      },
    },
    {
      id: "term",
      accessorFn: (row) => getTerm(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Total Months" />,
      cell: ({ row }) => getTerm(row.original) || "-",
    },
    {
      id: "startDate",
      accessorFn: (row) => getStartDate(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Start Date" />,
      cell: ({ row }) => dateLabel(getStartDate(row.original)),
    },
    {
      id: "endDate",
      accessorFn: (row) => getEndDate(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="End Date" />,
      cell: ({ row }) => dateLabel(getEndDate(row.original)),
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => <LoanStatusBadge status={row.original.status} />,
    },
    {
      id: "balance",
      accessorFn: (row) => getBalance(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Balance (FRW)" />,
      cell: ({ row }) => <div className="text-right font-bold">{getBalance(row.original).toLocaleString()}</div>,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const loan = row.original;
        return (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8" onClick={() => onView(loan)}>
              View Details
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8">
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onView(loan)}>
                  <Eye className="h-4 w-4" />
                  View details
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], [onView]);

  return (
    <div className="rounded-lg border border-emerald-100 bg-white">
      <div className="border-b border-emerald-100 bg-emerald-50/30 px-4 py-3">
        <h2 className="text-sm font-bold">{title}</h2>
      </div>
      <div className="p-3">
        <DataTable
          title={title}
          columns={columns}
          data={paginatedLoans}
          isLoading={false}
          defaultColumnVisibility={{}}
          onSearch={() => {}}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          sorting={sorting}
          setSorting={setSorting}
          setPagination={setPagination}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          pageCount={pageCount}
          enableExport={false}
          emptyMessage={emptyText}
          emptyDescription="Loan records from your account will appear here."
        />
      </div>
    </div>
  );
}

function LoanStatusBadge({ status }: { status: any }) {
  const key = statusKey(status);
  const label = key ? key.replace(/_/g, " ") : "unknown";
  return <Badge variant="outline" className={`rounded px-2 py-1 text-[11px] ${statusClasses[key] || "border-slate-200 bg-slate-50 text-slate-700"}`}>{label}</Badge>;
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return <label className="block"><span className="mb-2 block text-xs font-semibold text-[#4f5f8b]">{label}{required && <span className="text-red-500"> *</span>}</span>{children}</label>;
}

function SummaryLine({ label, value, strong, success }: { label: string; value: string; strong?: boolean; success?: boolean }) {
  return <div className="flex items-center justify-between gap-3 text-sm"><span className="text-[#4f5f8b]">{label}</span><span className={`text-right font-bold ${success ? "text-[#009A44]" : strong ? "text-[#07184f]" : "text-[#07184f]"}`}>{value}</span></div>;
}

function GuarantorRequests({ requests, loading, responding, compact, onRespond, onView }: { requests: any[]; loading: boolean; responding: boolean; compact?: boolean; onRespond: (id: number, action: "approve" | "reject") => void; onView: (loan: any) => void }) {
  if (loading) return <div className="flex h-28 items-center justify-center"><LoaderCircle className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (requests.length === 0) return <p className="py-6 text-center text-sm text-[#4f5f8b]">No guarantor requests awaiting your response.</p>;
  return (
    <div className="space-y-3">
      {requests.map((request) => (
        <div key={request.id} className="rounded-lg border border-blue-100 bg-blue-50/30 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600"><User className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[#07184f]">{request.loan?.applicant?.fullName || request.loan?.applicantName || "Member"}</p>
              <p className="text-xs text-[#4f5f8b]">is requesting you as guarantor</p>
              <Badge variant="outline" className="mt-2 rounded border-blue-200 bg-blue-50 text-blue-700">Loan ID: {request.loan?.loanCode || request.loanId || request.loan?.id}</Badge>
            </div>
          </div>
          {!compact && request.loan?.purpose && <p className="mt-3 text-sm text-[#4f5f8b]">{request.loan.purpose}</p>}
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <SummaryLine label="Loan Type" value={request.loan?.loanType || request.loan?.type || "-"} />
            <SummaryLine label="Amount Requested" value={money(request.loan?.amount)} />
            <SummaryLine label="Months" value={`${asNumber(request.loan?.termMonths) || "-"} Months`} />
            <SummaryLine label="Date of Request" value={dateLabel(request.createdAt)} />
          </div>
          <div className="mt-4 flex gap-2">
            <Button className="flex-1 bg-[#009A44] hover:bg-[#00843a]" disabled={responding} onClick={() => onRespond(request.id, "approve")}><CheckCircle2 className="mr-2 h-4 w-4" />Approve</Button>
            <Button variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50" disabled={responding} onClick={() => onRespond(request.id, "reject")}><XCircle className="mr-2 h-4 w-4" />Reject</Button>
          </div>
          {!compact && <Button variant="ghost" className="mt-2 w-full" onClick={() => onView(request.loan)}>View Loan Details</Button>}
        </div>
      ))}
    </div>
  );
}

function daysUntil(value: any) {
  const date = asDate(value);
  if (!date) return undefined;
  const diff = Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return `${Math.abs(diff)} days overdue`;
  if (diff === 0) return "Due today";
  return `${diff} days left`;
}
